/**
 * SlotApiService — reusable adapter for the A9OS slot REST API.
 * Works in any environment (Shopify theme, customer account extensions).
 */
window.SlotApiService = class SlotApiService {
  constructor(baseEndpoint) {
    this.base = baseEndpoint.replace(/\/$/, '');
  }

  // ── Slot grid ──────────────────────────────────────────────────────

  /**
   * Slot grid for a facility. `customerId` + `customerHash` (both or neither) make the backend answer
   * with `meta.membership` and a per-slot `membership_consumable_hours`. A rejected hash must never
   * cost the customer the grid, so the request is retried once without the identity.
   */
  async getSlots(facilityId, opts = {}) {
    const params = new URLSearchParams();
    params.set('from', opts.from);
    params.set('to', opts.to);
    params.set('duration', String(opts.duration));

    if (opts.suiteCount) params.set('suite_count', String(opts.suiteCount));
    if (opts.takeover) params.set('takeover', '1');
    if (opts.instructorId) params.set('instructor_id', String(opts.instructorId));
    if (opts.token) params.set('token', String(opts.token));
    if (opts.extensionBookingId) params.set('extension_booking_id', String(opts.extensionBookingId));
    if (opts.clubs) this._appendClubsParams(params, opts.clubs);

    const identified = opts.customerId != null && String(opts.customerId) !== '' && !!opts.customerHash;
    if (!identified) return this._requestSlots(facilityId, params);

    const identifiedParams = new URLSearchParams(params);
    identifiedParams.set('customer_id', String(opts.customerId));
    identifiedParams.set('customer_hash', String(opts.customerHash));

    try {
      return await this._requestSlots(facilityId, identifiedParams);
    } catch (error) {
      // 403 = bad hash, 422 = a backend that does not know these params yet.
      if (error && (error.status === 403 || error.status === 422)) {
        return this._requestSlots(facilityId, params);
      }
      throw error;
    }
  }

  async _requestSlots(facilityId, params) {
    const res = await fetch(`${this.base}/${facilityId}/slots?${params}`, {
      headers: { Accept: 'application/json' }
    });
    return this._handleResponse(res);
  }

  async getRescheduleSlots(bookingId, opts = {}) {
    const params = new URLSearchParams();
    params.set('id', String(opts.customerId));
    params.set('from', opts.from);
    params.set('to', opts.to);
    params.set('duration', String(opts.duration));

    if (opts.takeover) params.set('takeover', '1');
    if (opts.instructorId) params.set('instructor_id', String(opts.instructorId));
    if (opts.clubs) this._appendClubsParams(params, opts.clubs);

    const accountBase = SlotApiService.accountApiBase(this.base);
    const res = await fetch(`${accountBase}/reservations/${bookingId}/slots?${params}`, {
      headers: {
        Accept: 'application/json',
        Authorization: opts.authHash
      }
    });
    return this._handleResponse(res);
  }

  // ── Hold lifecycle ─────────────────────────────────────────────────

  async createHold(facilityId, body) {
    const payload = { ...body };
    if (payload.from) payload.from = SlotApiService.normalizeZulu(payload.from);
    if (payload.to) payload.to = SlotApiService.normalizeZulu(payload.to);
    const res = await fetch(`${this.base}/${facilityId}/slots`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });
    return this._handleResponse(res);
  }

  async releaseHold(facilityId, key, opts = {}) {
    const params = new URLSearchParams();
    const fromNormalized = SlotApiService.normalizeZulu(opts.from);
    if (fromNormalized != null && fromNormalized !== '') {
      params.set('from', fromNormalized);
    }
    if (opts.takeover) {
      params.set('takeover', '1');
    } else if (opts.suiteCombinationSetId != null) {
      params.set('suite_combination_set_id', String(opts.suiteCombinationSetId));
    } else if (opts.suiteId != null) {
      params.set('suite_id', String(opts.suiteId));
    }

    await fetch(`${this.base}/${facilityId}/slots/${encodeURIComponent(key)}/hold?${params}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' }
    });
  }

  async extendHold(facilityId, key, opts = {}) {
    const body = {};
    const fromNormalized = SlotApiService.normalizeZulu(opts.from);
    if (fromNormalized != null && fromNormalized !== '') body.from = fromNormalized;
    if (opts.suiteCombinationSetId != null) {
      body.suite_combination_set_id = opts.suiteCombinationSetId;
    } else if (opts.suiteId != null) {
      body.suite_id = opts.suiteId;
    }

    const res = await fetch(`${this.base}/${facilityId}/slots/${encodeURIComponent(key)}/hold`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (res.status === 404) return null;
    return this._handleResponse(res);
  }

  // ── Reschedule ─────────────────────────────────────────────────────

  async reschedule(bookingId, body, authHash) {
    const accountBase = SlotApiService.accountApiBase(this.base);
    const payload = { ...body };
    if (payload.from) payload.from = SlotApiService.normalizeZulu(payload.from);
    if (payload.to) payload.to = SlotApiService.normalizeZulu(payload.to);
    const res = await fetch(`${accountBase}/reservations/${bookingId}/reschedule`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: authHash
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      return this._handleResponse(res);
    }
    return {};
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _appendClubsParams(params, clubs) {
    params.set('clubs[left][adult]', String(clubs.left.adult));
    params.set('clubs[left][kid]', String(clubs.left.kid));
    params.set('clubs[right][adult]', String(clubs.right.adult));
    params.set('clubs[right][kid]', String(clubs.right.kid));
  }

  static clubsFromLegacy(clubsData) {
    return {
      left:  { adult: clubsData.club_left.adult_clubs,  kid: clubsData.club_left.kid_clubs },
      right: { adult: clubsData.club_right.adult_clubs, kid: clubsData.club_right.kid_clubs }
    };
  }

  static hasClubs(clubs) {
    return clubs && (clubs.left.adult > 0 || clubs.left.kid > 0 || clubs.right.adult > 0 || clubs.right.kid > 0);
  }

  /** `…/api/account` ↔ `…/api/facilities` (trailing slash optional). */
  static facilitiesApiBase(apiUrl) {
    const s = String(apiUrl || '').replace(/\/$/, '');
    if (/\/account$/i.test(s)) return s.replace(/\/account$/i, '/facilities');
    if (/\/facilities$/i.test(s)) return s;
    return `${s}/facilities`;
  }

  static accountApiBase(apiUrl) {
    const s = String(apiUrl || '').replace(/\/$/, '');
    if (/\/facilities$/i.test(s)) return s.replace(/\/facilities$/i, '/account');
    if (/\/account$/i.test(s)) return s;
    return `${s}/account`;
  }

  /**
   * Session object for account edit / reschedule (calendar + GET …/slots + POST …/reschedule).
   * @param {object} reservation — row from GET /account/reservations
   */
  static buildRescheduleSession(reservation, customerId, authHash) {
    if (!reservation) return null;
    const instructor = reservation.instructor || null;
    return {
      id: reservation.id,
      customer_id: customerId,
      hash: authHash,
      time_from: reservation.time_from,
      time_to: reservation.time_to,
      instructor,
      instructor_reservation_id: instructor?.id ?? reservation.instructor_reservation_id ?? null,
      variant_id: reservation.variant_id,
      takeover: reservation.takeover,
      duration_seconds: reservation.duration_seconds
    };
  }

  static toZulu(ms) {
    if (window.A9DateUtils && typeof window.A9DateUtils.toZuluMs === 'function') {
      return window.A9DateUtils.toZuluMs(ms);
    }
    return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  /**
   * API accepts only Zulu without milliseconds. Normalizes legacy cart/order strings (+00:00, .fffZ).
   */
  static normalizeZulu(iso) {
    if (!iso || typeof iso !== 'string') return iso;
    let s = iso.trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.\d{3}(Z|[+-]\d{2}:\d{2})$/);
    if (m) s = m[1] + (m[2] === 'Z' ? 'Z' : m[2]);
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2})$/.test(s)) {
      try {
        const d = new Date(s);
        if (!isNaN(d.getTime())) return SlotApiService.toZulu(d.getTime());
      } catch (e) { /* fall through */ }
    }
    return s;
  }

  /**
   * Facility-local midnight → Zulu for GET /slots window. dateStr must be `YYYY-MM-DD`.
   */
  static dayBoundsZulu(dateStr, tz) {
    if (typeof window.ensureA9DayjsPlugins === 'function') {
      window.ensureA9DayjsPlugins();
    }
    if (window.A9DateUtils && typeof window.A9DateUtils.dayBoundsZulu === 'function') {
      return window.A9DateUtils.dayBoundsZulu(dateStr, tz);
    }
    const z = String(tz || '').trim();
    const ymd = String(dateStr || '').trim();
    if (!z || !ymd) {
      throw new Error('SlotApiService.dayBoundsZulu: missing timezone or date');
    }
    const dayStart = dayjs.tz(ymd + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
    if (!dayStart.isValid()) {
      throw new Error('SlotApiService.dayBoundsZulu: invalid date or timezone');
    }
    const endYmd = dayjs(ymd, 'YYYY-MM-DD', true).add(1, 'day').format('YYYY-MM-DD');
    const dayEnd = dayjs.tz(endYmd + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
    if (!dayEnd.isValid()) {
      throw new Error('SlotApiService.dayBoundsZulu: invalid end date or timezone');
    }
    return {
      from: dayStart.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z',
      to: dayEnd.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z'
    };
  }

  /** Minutes since midnight from labels like "9:00 AM" / "09:00 PM" (theme timelist). */
  static parseTimelistLabelToMinutes(label) {
    if (!label || typeof label !== 'string') return null;
    const m = label.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (h < 1 || h > 12 || min < 0 || min > 59) return null;
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return h * 60 + min;
  }

  /** Cached wall-clock index. Grids are immutable — refetch, don't edit a row; else call invalidateAvailabilityIndex. */
  static _availabilityIndex(slots, tz) {
    if (!Array.isArray(slots) || !tz || typeof dayjs === 'undefined') return null;
    const zone = String(tz).trim();
    if (!zone) return null;

    let cache = SlotApiService._availabilityCache;
    if (!cache) {
      cache = new WeakMap();
      SlotApiService._availabilityCache = cache;
    }

    let indexByZone = cache.get(slots);
    if (!indexByZone) {
      indexByZone = new Map();
      cache.set(slots, indexByZone);
    }

    const cached = indexByZone.get(zone);
    if (cached) return cached;

    const index = {
      byMinute: new Map(),
      rowByMinute: new Map(),
      availableRowByMinute: new Map()
    };

    slots.forEach((row) => {
      if (!row || !row.from) return;
      const local = dayjs.utc(SlotApiService.normalizeZulu(row.from)).tz(zone);
      if (!local.isValid()) return;
      const minutes = local.hour() * 60 + local.minute();
      const isAvailable = !!row.available;

      index.byMinute.set(
        minutes,
        index.byMinute.has(minutes) ? index.byMinute.get(minutes) && isAvailable : isAvailable
      );

      if (!index.rowByMinute.has(minutes)) index.rowByMinute.set(minutes, row);
      if (isAvailable && !index.availableRowByMinute.has(minutes)) {
        index.availableRowByMinute.set(minutes, row);
      }
    });

    indexByZone.set(zone, index);
    return index;
  }

  /** Drop the cached index after an in-place row edit. */
  static invalidateAvailabilityIndex(slots) {
    const cache = SlotApiService._availabilityCache;
    if (cache && slots && typeof slots === 'object') cache.delete(slots);
  }

  /** Copy of the cached map so callers cannot mutate the index. */
  static buildAvailabilityByLocalStartMinute(slots, tz) {
    const index = SlotApiService._availabilityIndex(slots, tz);
    return index ? new Map(index.byMinute) : new Map();
  }

  /** The raw grid row whose `from` matches a Zulu instant. */
  static findSlotRowByFrom(slots, fromZulu, opts = {}) {
    if (!Array.isArray(slots) || !fromZulu) return null;
    const target = SlotApiService.normalizeZulu(fromZulu);
    return slots.find((row) => {
      if (!row || !row.from) return false;
      if (opts.requireAvailable && row.available !== true) return false;
      return SlotApiService.normalizeZulu(row.from) === target;
    }) || null;
  }

  /** The raw grid row whose start matches a timelist label in facility TZ. */
  static findSlotRowByLocalStartMinute(slots, tz, timeLabel, opts = {}) {
    if (!Array.isArray(slots) || !tz) return null;
    const mins = SlotApiService.parseTimelistLabelToMinutes(timeLabel);
    if (mins == null) return null;
    const index = SlotApiService._availabilityIndex(slots, tz);
    if (!index) return null;
    const rows = opts.requireAvailable ? index.availableRowByMinute : index.rowByMinute;
    return rows.get(mins) || null;
  }

  static findAvailableSlotByLocalStartMinute(slots, tz, timeLabel) {
    const row = SlotApiService.findSlotRowByLocalStartMinute(slots, tz, timeLabel, { requireAvailable: true });
    if (!row) return null;
    return {
      from: SlotApiService.normalizeZulu(row.from),
      to: SlotApiService.normalizeZulu(row.to)
    };
  }

  /**
   * Response `meta`, or null. It rides the returned array non-enumerably, so `.filter()` / `.map()`
   * drop it — read it off the payload before projecting the rows.
   */
  static readMeta(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const meta = payload.meta;
    return meta && typeof meta === 'object' ? meta : null;
  }

  /** `meta.membership`: an object when the customer has one, null when identified without one. */
  static readMembershipMeta(payload) {
    const meta = SlotApiService.readMeta(payload);
    if (!meta || !Object.prototype.hasOwnProperty.call(meta, 'membership')) return null;
    return meta.membership || null;
  }

  /** Whether the backend answered the membership question at all (guest / degraded → false). */
  static hasMembershipMeta(payload) {
    const meta = SlotApiService.readMeta(payload);
    return !!meta && Object.prototype.hasOwnProperty.call(meta, 'membership');
  }

  /** Hours of one slot the membership can pay for. `null` = unknown, `0` = it covers nothing. */
  static membershipConsumableHours(slotRow) {
    if (!slotRow || typeof slotRow !== 'object') return null;
    const raw = slotRow.membership_consumable_hours;
    if (raw === null || raw === undefined || raw === '') return null;
    const hours = Number(raw);
    return Number.isFinite(hours) ? hours : null;
  }

  static isStartTimeUnavailable(slots, tz, label) {
    if (!Array.isArray(slots) || !tz) return false;
    const mins = SlotApiService.parseTimelistLabelToMinutes(label);
    if (mins === null) return false;
    const index = SlotApiService._availabilityIndex(slots, tz);
    if (!index) return false;
    return index.byMinute.get(mins) === false;
  }

  static isStartTimeDstSkipped(label, ymd, tz) {
    if (!ymd || !tz) return false;
    const dateUtils = window.A9DateUtils;
    if (!dateUtils || typeof dateUtils.isWallTimeSkippedByDst !== 'function') return false;
    const mins = SlotApiService.parseTimelistLabelToMinutes(label);
    if (mins === null) return false;
    const minutesPerHour = 60;
    return dateUtils.isWallTimeSkippedByDst(ymd, Math.floor(mins / minutesPerHour), mins % minutesPerHour, tz);
  }

  /**
   * Disable timelist nodes the backend marks unavailable (no client-side occupancy math).
   */
  static applyAvailabilityToTimelist(timeItemElements, slots, tz) {
    if (!timeItemElements?.length || !tz) return;
    timeItemElements.forEach((el) => {
      const label = el.getAttribute('data-value')?.trim();
      if (label && SlotApiService.isStartTimeUnavailable(slots, tz, label)) {
        el.classList.add('is-disabled');
      }
    });
  }

  /**
   * Disable timelist entries whose start time falls in the spring-forward DST gap.
   * @param {NodeList|Array} timeItemElements — timelist nodes (data-value = "h:mm AM")
   * @param {string} ymd — selected calendar day as YYYY-MM-DD
   * @param {string} tz — facility IANA timezone
   */
  static disableDstSkippedTimes(timeItemElements, ymd, tz) {
    if (!timeItemElements?.length || !ymd || !tz) return;

    const dateUtils = window.A9DateUtils;
    if (dateUtils && typeof dateUtils.hasDstTransitionOnDay === 'function'
      && !dateUtils.hasDstTransitionOnDay(ymd, tz)) {
      return;
    }

    timeItemElements.forEach((el) => {
      const label = el.getAttribute('data-value')?.trim();
      if (label && SlotApiService.isStartTimeDstSkipped(label, ymd, tz)) {
        el.classList.add('is-disabled');
      }
    });
  }

  async _handleResponse(res) {
    if (res.status === 204) return null;

    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch { json = { message: text }; }

    if (!res.ok) {
      let errorMessage = json.message || 'Request failed';
      if (json.errors) {
        const msgs = [];
        for (const field in json.errors) {
          if (Array.isArray(json.errors[field])) msgs.push(...json.errors[field]);
        }
        if (msgs.length) errorMessage = msgs.join('. ');
      }
      const err = new Error(errorMessage);
      err.status = res.status;
      err.errors = json.errors || null;
      throw err;
    }

    const data = json.data !== undefined ? json.data : json;
    return SlotApiService.attachResponseMeta(data, json.meta);
  }

  /**
   * Carries the envelope's `meta` on the returned payload without changing its shape — the slot grid
   * must stay a plain array for every existing caller, the account extension included.
   */
  static attachResponseMeta(data, meta) {
    if (meta === undefined || !data || typeof data !== 'object') return data;
    try {
      Object.defineProperty(data, 'meta', { value: meta, enumerable: false, configurable: true });
    } catch (error) {
      /* sealed payload — meta is unavailable */
    }
    return data;
  }
};
